// Season shares: a standing arrangement that every breeding plan one person
// owns in a season (or in every season) is shared with another. It is a
// record, not a list of plans — so a plan created next month in that season
// is shared the moment it exists, and nothing goes stale.
//
// The recipient has to accept. Sharing a whole season is a lot to hand
// someone, and a push that says "Jane wants to share her 2027 plans with you"
// is the polite way in; nothing reaches them until they say yes. Sharing a
// single plan stays the one-off it always was, for when that is all you want.
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  onSnapshot,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './config';

const sharesRef = collection(db, 'planSeasonShares');

const person = (u) => ({ email: u?.email || '', name: u?.name || u?.displayName || u?.email || '' });

// `year` is a number, or 'all' for every season. `applied` flips once the
// owner's device has shared the plans that already existed; `cleaned` once it
// has taken them back after the share ends.
export const inviteSeasonShare = (owner, to, year, permission) =>
  addDoc(sharesRef, {
    ownerId: owner.uid,
    toUid: to.uid,
    uids: [owner.uid, to.uid],
    people: { [owner.uid]: person(owner), [to.uid]: person(to) },
    year: year === 'all' ? 'all' : Number(year),
    permission: permission === 'full' ? 'full' : 'view',
    status: 'pending',
    applied: false,
    cleaned: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

export const acceptSeasonShare = (id) =>
  updateDoc(doc(sharesRef, id), { status: 'active', updatedAt: serverTimestamp() });

// Declining a pending invite removes it — nothing was ever shared.
export const declineSeasonShare = (id) => deleteDoc(doc(sharesRef, id));

// Either side may end it. The owner's device then takes the plans back.
export const endSeasonShare = (id, byUid) =>
  updateDoc(doc(sharesRef, id), { status: 'ended', endedBy: byUid, updatedAt: serverTimestamp() });

export const markSeasonShareApplied = (id) =>
  updateDoc(doc(sharesRef, id), { applied: true, updatedAt: serverTimestamp() });

export const removeSeasonShare = (id) => deleteDoc(doc(sharesRef, id));

export const subscribeToSeasonShares = (uid, callback) => {
  if (!uid) { callback([]); return () => {}; }
  return onSnapshot(query(sharesRef, where('uids', 'array-contains', uid)), (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  }, () => { /* keep last-known state on transient error */ });
};

// Whether a share reaches a plan: same owner, and the plan's season is the
// share's season (or the share covers every season).
export const seasonShareCovers = (share, plan) =>
  !!share && !!plan
  && plan.ownerId === share.ownerId
  && (share.year === 'all' || Number(plan.year) === Number(share.year));

// The active shares an owner has given, fetched once when a plan is created so
// the new plan starts out shared. Filtered here rather than in the query —
// array-contains plus an equality needs a composite index, and there are few.
export const fetchActiveSeasonSharesBy = async (ownerId) => {
  if (!ownerId) return [];
  const snap = await getDocs(query(sharesRef, where('uids', 'array-contains', ownerId)));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((s) => s.ownerId === ownerId && s.status === 'active');
};

// What a share entry on a plan looks like for a given season share.
export const shareEntryFor = (s) => {
  const p = s.people?.[s.toUid] || {};
  return { uid: s.toUid, email: p.email || '', name: p.name || p.email || '', permission: s.permission, viaSeasonShare: s.id };
};

export const seasonLabel = (s) => (s?.year === 'all' ? 'every season' : String(s?.year || ''));
